// src/app/pages/dashboard/components/analysis/analysis.component.ts
import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ChartModule } from 'primeng/chart';
import { combineLatest, map } from 'rxjs';

import { AnalysisStore, MetricValue, GroupValue } from '../../../../core/shared/analysis.store';
import { YearStore } from '../../../../core/shared/year.store';
import { InterventionStore } from '../../../../core/shared/intervention.store';

type SeriesPoint = { x: number; y: number };

@Component({
  selector: 'app-analysis',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule, ChartModule],
  templateUrl: './analysis.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnalysisComponent {
  private store = inject(AnalysisStore);
  private yearStore = inject(YearStore);
  private interventionStore = inject(InterventionStore);

  minYearForDisplay$ = this.yearStore.displayMinYear$;
  selectedYear$ = this.yearStore.year$;

  metricOptions: { label: string; value: MetricValue }[] = [
    { label: 'Controlled', value: 'controlled' },
    { label: 'Treated not controlled', value: 'treated_not_controlled' },
    { label: 'Diagnosed', value: 'diagnosed' },
    { label: 'Undiagnosed', value: 'undiagnosed' },
    { label: 'Prevalence', value: 'prevalence' },
    { label: 'Screened', value: 'screened' },
    { label: 'Aware of screening', value: 'aware_of_screening' },
    { label: 'CVD events', value: 'cvd_events' },
    { label: 'CVD patients', value: 'cvd_patients' },
    { label: 'CVD deaths', value: 'cvd_deaths' }
  ];

  groupOptions: { label: string; value: GroupValue }[] = [
    { label: 'Total', value: 'total' },
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
    { label: 'Average', value: 'average' }
  ];

  displayRange$ = combineLatest([this.yearStore.displayMinYear$, this.yearStore.year$]).pipe(
    map(([min, max]) => (Number(min) === Number(max) ? String(max) : `${min} to ${max}`))
  );

  get metric(): MetricValue { return this.store.metric$.value; }
  set metric(v: MetricValue) { this.store.metric$.next(v); }

  get group(): GroupValue { return this.store.group$.value; }
  set group(v: GroupValue) { this.store.group$.next(v); }

  // Selected simulation name (for display)
  selectedSimulation$ = combineLatest([this.store.selectedRunId$, this.store.runsList$]).pipe(
    map(([id, runs]) => {
      const run = id ? runs.find(r => r.id === id) : undefined;
      return run?.title ?? '-';
    })
  );

  // Active interventions count (computed from selectedOptions in snapshot)
  activeInterventionsCount$ = this.interventionStore.activeCount$;

  // Minimal changes in src/app/pages/dashboard/components/analysis/analysis.component.ts

  chart$ = combineLatest([this.store.combinedSeries$, this.yearStore.displayMinYear$, this.yearStore.year$]).pipe(
    map(([seriesList, displayMinYear, selectedYear]) => {
      const safeSeries = Array.isArray(seriesList) ? seriesList : [];
      const xMin = Number(displayMinYear);
      const xMax = Number(selectedYear);
      const rangeYears = Math.abs(xMax - xMin);
      const isShortRange = rangeYears < 10;
      const targetTicks = 8;
      const stepForLong = Math.max(1, Math.round(rangeYears / (targetTicks - 1)));

      const datasets = safeSeries.map((s: any) => ({
        label: s.label,
        data: (s.points || []).filter((p: any) => typeof p?.x === 'number' && p.x >= xMin && p.x <= xMax),
        borderColor: s.color || '#ef4444',
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.3
      }));

      const data = { datasets };
      const options: any = {
        responsive: true,
        maintainAspectRatio: true,
        parsing: true,
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              color: 'var(--text-color, #334155)',
              usePointStyle: true,
              boxWidth: 10,
              font: { size: 10 }
            }
          },
          tooltip: {
            mode: 'nearest',
            intersect: false,
            callbacks: {
              title: (items: any[]) => `Year: ${String(items[0]?.raw?.x)}`,
              label: (ctx: any) => {
                const v = ctx.raw?.y ?? 0;
                const m = v / 1_000_000;
                return `${ctx.dataset.label}: ${m.toFixed(2)}M`;
              }
            }
          }
        },
        scales: {
          x: {
            type: 'linear',
            min: xMin,
            max: xMax,
            ticks: {
              stepSize: isShortRange ? 1 : stepForLong,
              autoSkip: false,
              precision: 0,
              color: 'var(--text-secondary-color, #64748B)',
              callback: (value: number) => String(Math.round(value)),
              font: { size: 10 }
            },
            grid: { color: 'rgba(100,116,139,0.1)' }
          },
          y: {
            beginAtZero: true,
            ticks: {
              color: 'var(--text-secondary-color, #64748B)',
              callback: (v: number) => new Intl.NumberFormat('en-US', { notation: 'compact' }).format(v),
              font: { size: 10 }
            },
            grid: { color: 'rgba(100,116,139,0.1)' }
          }
        }
      };

      return { data, options };
    })
  );

  cascade$ = combineLatest([this.store.runsList$, this.yearStore.year$, this.store.selectedRunId$]).pipe(
    map(([runs, selectedYear, selectedId]) => {
      const labels = ['Total HT Population', 'Undiagnosed', 'Diagnosed', 'Treated', 'Controlled'];

      const run = runs.find(r => r.id === selectedId) || (runs.length ? runs[runs.length - 1] : null);
      const rows = Array.isArray(run?.rows) ? run?.rows : [];
      const rowForYear = rows.find((r: any) => Number(r.year) === Number(selectedYear)) || rows[rows.length - 1] || null;

      const dataEmpty = {
        data: {
          labels,
          datasets: [{
            label: '',
            data: [0, 0, 0, 0, 0],
            backgroundColor: ['#550c22', '#aa1646', '#d41c58', '#eb6e97', '#fec2d6', '#586480'],
            borderColor: ['#550c22', '#aa1646', '#d41c58', '#eb6e97', '#fec2d6', '#586480'],
            borderWidth: 1
          }]
        },
        options: this.cascadeBarOptionsSimple(Number(selectedYear) || 2011, 1)
      };

      if (!rowForYear) return dataEmpty;

      const diagnosed = Number(rowForYear.diagnosed_total ?? 0);
      const undiagnosed = Number(rowForYear.undiagnosed_total ?? 0);
      const treated = Number(rowForYear.treated_total ?? 0);
      const controlled = Number(
        rowForYear.controlled_total ??
        (Number(rowForYear.controlled_male ?? 0) + Number(rowForYear.controlled_female ?? 0))
      );
      const totalHtPopulation = diagnosed + undiagnosed;

      const values = [totalHtPopulation, undiagnosed, diagnosed, treated, controlled];
      const suggestedMax = Math.ceil(Math.max(...values, 1) * 1.2);
      const displayYear = Number(selectedYear) || Number(rowForYear.year) || 2011;

      return {
        data: {
          labels,
          datasets: [{
            label: '',
            data: values,
            backgroundColor: ['#550c22', '#aa1646', '#d41c58', '#eb6e97', '#fec2d6', '#586480'],
            borderColor: ['#550c22', '#aa1646', '#d41c58', '#eb6e97', '#fec2d6', '#586480'],
            borderWidth: 1
          }]
        },
        options: this.cascadeBarOptionsSimple(displayYear, suggestedMax)
      };
    })
  );

  // Minimal change: update cascadeBarOptionsSimple to show bar labels (in Millions) and Y-axis in Millions
  private cascadeBarOptionsSimple(latestYear: number, suggestedMax: number) {
    const legendLabels = ['Total HT Population', 'Undiagnosed', 'Diagnosed', 'Treated', 'Controlled'];
    const legendColors = ['#550c22', '#aa1646', '#d41c58', '#eb6e97', '#fec2d6', '#586480'];

    return {
      responsive: false,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            generateLabels: () => legendLabels.map((text, idx) => ({
              text, fillStyle: legendColors[idx], strokeStyle: legendColors[idx], hidden: false, index: idx
            })),
            color: 'var(--text-color, #334155)',
            boxWidth: 12, boxHeight: 12, padding: 10, font: { size: 11 }
          }
        },
        tooltip: {
          mode: 'index',
          intersect: true,
          callbacks: {
            title: (items: any[]) => items?.[0]?.label ?? '',
            label: (ctx: any) => {
              const v = Number(ctx.parsed?.y ?? 0);
              return `${ctx.label}: ${(v / 1_000_000).toFixed(2)}M`;
            }
          }
        }
      },
      scales: {
        x: { display: false, grid: { display: false }, border: { display: false } },
        y: {
          beginAtZero: true,
          suggestedMax,
          // Y-axis ticks in Millions
          ticks: {
            color: 'var(--text-secondary-color, #64748B)',
            font: { size: 10 },
            callback: (v: number) => `${(Number(v) / 1_000_000).toFixed(2)}M`
          },
          grid: { color: 'rgba(100,116,139,0.12)' },
          border: { display: false }
        }
      },
      barPercentage: 0.5,
      // Draw value labels (Millions) on top of each bar without extra plugins
      animation: {
        duration: 500,
        easing: 'easeOutQuart',
        onComplete(ctx: any) {
          const chart = ctx.chart;
          const { ctx: c } = chart;
          c.save();
          c.textAlign = 'center';
          c.textBaseline = 'bottom';
          c.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--text-color') || '#334155';
          c.font = '600 10px sans-serif';

          chart.data.datasets.forEach((ds: any, di: number) => {
            const meta = chart.getDatasetMeta(di);
            meta.data.forEach((bar: any, i: number) => {
              const rawVal = Number(ds.data?.[i] ?? 0);
              const label = `${(rawVal / 1_000_000).toFixed(2)}M`;
              const x = bar.x;
              const y = bar.y - 4; // small padding above bar
              c.fillText(label, x, y);
            });
          });

          c.restore();
        }
      }
    };
  }
}